import {
  FinanceBudgetsDetailBudgetFragment,
  FinanceBudgetAmountFilter,
  FinanceBudgetsBudgetAmountWidgetsFragment,
  FinanceBudgetDetailAmountWidgetsDocument,
} from 'generated/resources/graphql'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import Money from 'modules/expenses/pages/ExpensesReportsPage/Money'
import i18n from 'modules/core/lib/i18n'
import { useQuery } from 'modules/core/hooks/useQuery'
import { budgetStatusTextColor } from 'modules/finance/lib/budgets'

interface Props {
  budget: FinanceBudgetsDetailBudgetFragment
  initialAmounts: FinanceBudgetsBudgetAmountWidgetsFragment
  filters?: FinanceBudgetAmountFilter
  status?: string
}

export const BudgetAmountWidgets = ({ budget, initialAmounts, filters, status }: Props) => {
  const hasFilters = filters && Object.keys(filters).length > 0

  const { data, loading } = useQuery(FinanceBudgetDetailAmountWidgetsDocument, {
    variables: {
      budgetId: budget.id,
      filters,
    },
    skip: !hasFilters,
  })

  const filteredAmounts = data?.finance.budgetAmountsConnection.nodes[0]

  const total = initialAmounts.budgetedAmountCents
  const available = initialAmounts.availableAmountCents
  const currency = budget.currency

  const committed =
    hasFilters && filteredAmounts
      ? filteredAmounts?.committedAmountCents
      : initialAmounts.committedAmountCents
  const pending =
    hasFilters && filteredAmounts
      ? filteredAmounts?.pendingAmountCents
      : initialAmounts.pendingAmountCents
  const spent =
    hasFilters && filteredAmounts
      ? filteredAmounts?.spentAmountCents
      : initialAmounts.spentAmountCents

  const committedByStatus = status
    ? {
        spent: spent,
        pending: pending,
      }[status] || committed
    : committed

  const availableColor = budgetStatusTextColor(budget.currentStatus)

  return (
    <Box width="full" flexDirection="row" gap="s16">
      <Box width="full">
        {
          <Widget>
            <Box flexDirection="column" gap="s8">
              <Text size="300" weight="semibold" color="grey1000">
                {i18n.t('budgets.detail_budget_amount_widget.total')}
              </Text>
              <Text.Header1>
                <Money amount={total} currency={currency} />
              </Text.Header1>
            </Box>
          </Widget>
        }
      </Box>
      <Box width="full">
        <Widget>
          <Box flexDirection="column" gap="s8">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('budgets.detail_budget_amount_widget.committed')}
            </Text>
            <Text.Header1>
              <Money amount={committedByStatus} currency={currency} />
            </Text.Header1>
          </Box>
        </Widget>
      </Box>
      <Box width="full">
        <Widget>
          <Box flexDirection="column" gap="s8">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('budgets.detail_budget_amount_widget.spent')}
            </Text>
            <Text.Header1>
              <Money amount={spent} currency={currency} />
            </Text.Header1>
          </Box>
        </Widget>
      </Box>
      <Box width="full">
        <Widget>
          <Box flexDirection="column" gap="s8">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('budgets.detail_budget_amount_widget.available')}
            </Text>
            <Text.Header1 color={availableColor}>
              <Money amount={available} currency={currency} />
            </Text.Header1>
          </Box>
        </Widget>
      </Box>
    </Box>
  )
}
